import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchSessionEvaluationVideos } from "../api/sessionMediaApi";
import {
  clipFilmedTableNumbers,
  parseTableNumberFromVideoUrl,
  resolveFeedbackSequenceOrder,
} from "../utils/evaluationVideoMatching";

function parseViewSetup(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "object") return Array.isArray(raw) ? null : raw;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch {
    return null;
  }
}

function positiveInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

function clipIdentityIds(clip, setup) {
  const ids = new Set();
  if (clip?.sender_id != null) ids.add(String(clip.sender_id));
  for (const entry of setup?.assigned_players || []) {
    for (const id of [
      entry?.player_id,
      entry?.playerId,
      entry?.user_id,
      entry?.userId,
    ]) {
      if (id != null) ids.add(String(id));
    }
  }
  for (const id of setup?.shared_user_ids || []) {
    if (id != null) ids.add(String(id));
  }
  return ids;
}

function tableSeatIds(table) {
  const ids = new Set();
  const seats = [
    table?.player1,
    table?.player2,
    table?.player3,
    ...(Array.isArray(table?.extras) ? table.extras : []),
  ];
  for (const seat of seats) {
    if (!seat) continue;
    for (const id of [seat.playerId, seat.player_id, seat.userId, seat.user_id]) {
      if (id != null) ids.add(String(id));
    }
  }
  return ids;
}

/**
 * Table number for a recorded clip: upload stamp, then SmartTT_T{n}_ filename,
 * then the table where the uploader / assigned players were seated.
 */
function resolveClipTableNumber(clip, setup, tables) {
  const stamped = positiveInt(setup?.table_number);
  if (stamped) return stamped;
  const fromUrl = parseTableNumberFromVideoUrl(clip?.video_url);
  if (fromUrl) return fromUrl;

  const ids = clipIdentityIds(clip, setup);
  if (ids.size) {
    const match = (tables || []).find((table) => {
      const seatIds = tableSeatIds(table);
      return [...ids].some((id) => seatIds.has(id));
    });
    const n = positiveInt(match?.tableNumber ?? match?.table_number);
    if (n) return n;
  }

  const numbered = (tables || [])
    .map((table) => positiveInt(table?.tableNumber ?? table?.table_number))
    .filter(Boolean);
  return numbered.length === 1 ? numbered[0] : null;
}

/**
 * Evaluation videos of a finalized exercise grouped by table number,
 * for playback from the coach table configuration.
 */
export function useCoachTableEvaluationVideos({
  tsId,
  teId,
  enabled = false,
  tables = [],
}) {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!tsId || !teId) {
      setClips([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSessionEvaluationVideos(tsId);
      const list = Array.isArray(data?.clips) ? data.clips : [];
      setClips(
        list.filter(
          (clip) => clip?.video_url && String(clip.te_id) === String(teId)
        )
      );
    } catch (e) {
      setClips([]);
      setError(e?.message || "Could not load evaluation videos");
    } finally {
      setLoading(false);
    }
  }, [tsId, teId]);

  useEffect(() => {
    setClips([]);
  }, [tsId, teId]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const clipsByTable = useMemo(() => {
    const byTable = {};
    if (!enabled) return byTable;
    for (const clip of clips) {
      const setup = parseViewSetup(clip.view_setup);
      const filmed = clipFilmedTableNumbers({
        ...clip,
        view_setup: setup,
        video_message_url: clip.video_message_url || clip.video_url,
      });
      const tableNumbers = filmed.length
        ? filmed
        : [resolveClipTableNumber(clip, setup, tables)].filter(Boolean);
      for (const tableNumber of tableNumbers) {
        const key = String(tableNumber);
        if (!byTable[key]) byTable[key] = [];
        byTable[key].push({
          ...clip,
          view_setup: setup,
          table_number: tableNumber,
          sequence_order: resolveFeedbackSequenceOrder({
            ...clip,
            view_setup: setup,
            video_message_url: clip.video_url,
          }),
          camera_position: setup?.camera_position || null,
        });
      }
    }
    return byTable;
  }, [enabled, clips, tables]);

  return { clipsByTable, loading, error, reload: load };
}

export default useCoachTableEvaluationVideos;
